/* eslint-disable react-hooks/immutability */

import {
  ColumnPinningState,
  functionalUpdate,
  type RowData,
  type TableOptions,
  type Updater,
} from '@tanstack/react-table';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo, useRef } from 'react';

import type {
  DataTableColumnPinningProps,
  DataTablePinnedColumnState,
} from './column-pinning-types.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import {
  DATA_TABLE_BUILT_IN_COLUMN_IDS,
  DATA_TABLE_ROW_ACTIONS_COLUMN_ID,
  DATA_TABLE_ROW_DETAILS_COLUMN_ID,
  DATA_TABLE_ROW_DRAG_COLUMN_ID,
  DATA_TABLE_ROW_SELECTION_COLUMN_ID,
} from '../../constants.js';
import { DataTableColumnOrderProps } from '../ColumnOrder/column-order-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';

/**
 * @returns true, if the given column is not a built-in column, false otherwise.
 */
function isCustomColumn(columnId: string): boolean {
  return !DATA_TABLE_BUILT_IN_COLUMN_IDS.has(columnId);
}

function removeBuiltinColumns(pinnedColumns: string[] | undefined): string[] {
  return pinnedColumns?.filter(isCustomColumn) ?? [];
}

function addBuiltinColumns(
  pinnedColumns: DataTablePinnedColumnState | undefined,
  options: {
    enableRowDetails: boolean;
    enableRowSelection: boolean;
    enableRowOrderDragAndDrop: boolean;
    enableRowActions: boolean;
  },
): ColumnPinningState {
  if (!pinnedColumns) {
    return {
      left: [],
      right: [],
    };
  }

  // remove any built-in columns first
  const leftPinnedColumns = removeBuiltinColumns(pinnedColumns.left);
  const rightPinnedColumns = removeBuiltinColumns(pinnedColumns.right);

  // add built-in columns based on enabled features, in correct pinning order
  if (options.enableRowDetails) {
    leftPinnedColumns.unshift(DATA_TABLE_ROW_DETAILS_COLUMN_ID);
  }
  if (options.enableRowSelection) {
    leftPinnedColumns.unshift(DATA_TABLE_ROW_SELECTION_COLUMN_ID);
  }
  if (options.enableRowOrderDragAndDrop) {
    leftPinnedColumns.unshift(DATA_TABLE_ROW_DRAG_COLUMN_ID);
  }
  if (options.enableRowActions) {
    rightPinnedColumns.push(DATA_TABLE_ROW_ACTIONS_COLUMN_ID);
  }

  return {
    left: leftPinnedColumns,
    right: rightPinnedColumns,
  };
}

/**
 * Configuration hook for the DataTable column pinning feature.
 * @internal
 */
export function useColumnPinning<TData extends RowData>(
  props: DataTableColumnPinningProps & DataTableColumnOrderProps,
  options: TableOptions<TDataTableFeatures, TData>,
): void {
  options.enableColumnPinning = props.columnPinning === true;

  const {
    enableColumnPinning,
    enableRowDetails,
    enableRowSelection,
    enableRowOrderDragAndDrop,
    enableRowActions,
  } = options;

  // uncontrolled
  const defaultPinnedColumns = useMemo(() => {
    // columns pinned via props in addition to built-in columns
    const userDefinedPinnedColumns =
      enableColumnPinning && props.defaultPinnedColumns
        ? props.defaultPinnedColumns
        : { left: [], right: [] };
    return addBuiltinColumns(userDefinedPinnedColumns, {
      enableRowDetails: !!enableRowDetails,
      enableRowSelection: !!enableRowSelection,
      enableRowOrderDragAndDrop: !!enableRowOrderDragAndDrop,
      enableRowActions: !!enableRowActions,
    });
  }, [
    enableColumnPinning,
    enableRowActions,
    enableRowDetails,
    enableRowOrderDragAndDrop,
    enableRowSelection,
    props.defaultPinnedColumns,
  ]);

  // controlled
  const pinnedColumns = useMemo(
    (): ColumnPinningState | undefined =>
      enableColumnPinning && props.pinnedColumns
        ? addBuiltinColumns(props.pinnedColumns, {
            enableRowDetails: !!enableRowDetails,
            enableRowSelection: !!enableRowSelection,
            enableRowOrderDragAndDrop: !!enableRowOrderDragAndDrop,
            enableRowActions: !!enableRowActions,
          })
        : undefined,
    [
      enableColumnPinning,
      enableRowActions,
      enableRowDetails,
      enableRowOrderDragAndDrop,
      enableRowSelection,
      props.pinnedColumns,
    ],
  );

  const [columnPinning, setColumnPinning] =
    useControllableState<ColumnPinningState>({
      value: pinnedColumns,
      defaultValue: defaultPinnedColumns,
    });

  options.state = {
    ...options.state,
    columnPinning: enableColumnPinning ? columnPinning : defaultPinnedColumns, // just built-in columns
  };

  options.onColumnPinningChange = useCallback(
    (updater: Updater<ColumnPinningState>) => {
      const nextState = functionalUpdate(updater, columnPinning);
      if (isEqual(nextState, columnPinning)) {
        // nothing to update
        return;
      }

      // user does not have to provide IDs of built-in columns (which are always pinned)
      setColumnPinning(
        addBuiltinColumns(nextState, {
          enableRowDetails: !!enableRowDetails,
          enableRowSelection: !!enableRowSelection,
          enableRowOrderDragAndDrop: !!enableRowOrderDragAndDrop,
          enableRowActions: !!enableRowActions,
        }),
      );
      // do not emit IDs of built-in columns (which are always pinned)
      props.onPinnedColumnsChange?.({
        left: removeBuiltinColumns(nextState.left),
        right: removeBuiltinColumns(nextState.right),
      });
    },
    [
      columnPinning,
      enableRowActions,
      enableRowDetails,
      enableRowOrderDragAndDrop,
      enableRowSelection,
      props,
      setColumnPinning,
    ],
  );

  const originalColumnPinningRef = useRef<DataTablePinnedColumnState>();

  if (!originalColumnPinningRef.current) {
    const originalPinned = pinnedColumns || defaultPinnedColumns || [];
    originalColumnPinningRef.current = {
      left: originalPinned.left,
      right: originalPinned.right,
    };
  }

  options.originalColumnPinning = originalColumnPinningRef.current;
}
